import { register, init, getLocaleFromNavigator, locale, addMessages } from 'svelte-i18n';
import { WindowsCustomEventTypes } from './lib/enums/windows-custom-event.enum';
import type { IConfigStore } from './lib/interfaces/store-config.interface';
import { defaultLogger as logger } from './lib/telemetry/logger';

// Import all locale files statically to bundle them into the output
import enLocale from './locales/en.json';
import deLocale from './locales/de.json';
import fiLocale from './locales/fi.json';
import svLocale from './locales/sv.json';
import itLocale from './locales/it.json';
import esLocale from './locales/es.json';
import nlLocale from './locales/nl.json';
import frLocale from './locales/fr.json';
import jaLocale from './locales/ja.json';
import taLocale from './locales/ta-in.json';
import ptLocale from './locales/pt.json';
import hiLocale from './locales/hi.json';
import zhLocale from './locales/zh-cn.json';
import zhHantLocale from './locales/zh-hant.json';
import zhHansLocale from './locales/zh-hans.json';
import zhHkLocale from './locales/zh-hk.json';
import zhTwLocale from './locales/zh-tw.json';
import zhSgLocale from './locales/zh-sg.json';
import zhMoLocale from './locales/zh-mo.json';
import arLocale from './locales/ar.json';
import koLocale from './locales/ko.json';
import thLocale from './locales/th.json';
import plLocale from './locales/pl.json';
import viLocale from './locales/vi.json';

export type SupportedLocale = 
  | 'en' | 'de' | 'fi' | 'sv' | 'it' | 'es' | 'nl' | 'fr' | 'ja' 
  | 'ta' | 'pt' | 'hi' | 'zh' | 'ta-in' | 'zh-cn' | 'zh-hk' 
  | 'zh-tw' | 'zh-sg' | 'zh-mo' | 'zh-hant' | 'zh-hans' 
  | 'ar' | 'ko' | 'th' | 'pl' | 'vi';

export interface I18nConfig {
  fallbackLocale?: SupportedLocale;
  initialLocale?: SupportedLocale | string;
}

export interface TranslationOverrides {
  [key: string]: string | TranslationOverrides;
}

declare global {
  interface Window {
    gqaLocale?: string;
  }
}

export class I18nManager {
  private isInitialized = false;
  private registeredLocales = new Set<string>();
  private translationOverrides: Map<string, TranslationOverrides> = new Map();

  constructor() {
    this.registerAllLocales();
    this.registerListeners();
  }

  private registerListeners(): void {
    window.addEventListener(WindowsCustomEventTypes.configReady, (e)  => {
        logger.debug('I18nManager: Received configReady event', e);
        const event = e as CustomEvent<IConfigStore>;
        if (event.detail?.i18n?.overrideTranslations) {
            logger.debug('I18nManager: Applying translation overrides from config', event.detail.i18n.overrideTranslations);
            const overrides = event.detail.i18n.overrideTranslations;
            if (overrides && typeof overrides === 'object') {
                for (const [locale, translations] of Object.entries(overrides)) {
                    this.overrideTranslations(locale as SupportedLocale, translations as { [key: string]: string | TranslationOverrides });
                }
            }
        }
        if (event.detail?.i18n?.preferredLocale && event.detail?.i18n?.preferredLocale !== this.getCurrentLocale()) {
            logger.debug('I18nManager: Switching to preferred locale from config', event.detail.i18n.preferredLocale);
            this.switchLocale(event.detail.i18n.preferredLocale);
        }
    });
  }

  private registerAllLocales(): void {
    // Register all locales with static imports (bundled into the output)
    const localeRegistrations: Record<SupportedLocale, any> = {
      'en': enLocale,
      'de': deLocale,
      'fi': fiLocale,
      'sv': svLocale,
      'it': itLocale,
      'es': esLocale,
      'nl': nlLocale,
      'fr': frLocale,
      'ja': jaLocale,
      'ta': taLocale,
      'pt': ptLocale,
      'hi': hiLocale,
      'zh': zhLocale,
      'ta-in': taLocale, // Use the same as 'ta'
      'zh-cn': zhLocale,
      'zh-hk': zhHkLocale,
      'zh-tw': zhTwLocale,
      'zh-sg': zhSgLocale,
      'zh-mo': zhMoLocale,
      'zh-hant': zhHantLocale,
      'zh-hans': zhHansLocale,
      'ar': arLocale,
      'ko': koLocale,
      'th': thLocale,
      'pl': plLocale,
      'vi': viLocale
    };

    Object.entries(localeRegistrations).forEach(([localeKey, localeData]) => {
      register(localeKey, () => Promise.resolve(localeData));
      this.registeredLocales.add(localeKey);
    });
  }

  private getCustomLocale(): string | null {
    let customLocale: string | null;
    
    if (window.gqaLocale) {
      customLocale = window.gqaLocale;
      delete window.gqaLocale;
      customLocale = customLocale.toLowerCase();
    } else {
      customLocale = getLocaleFromNavigator();
    }
    
    return customLocale;
  }

  public init(config?: I18nConfig): void {
    if (this.isInitialized) {
      logger.warn('I18nManager is already initialized');
      return;
    }

    const customLocale = this.getCustomLocale();
    
    init({
      fallbackLocale: config?.fallbackLocale || 'en',
      initialLocale: config?.initialLocale || customLocale,
    });

    this.isInitialized = true;
  }

  public switchLocale(newLocale: SupportedLocale | string): void {
    if (!this.isInitialized) {
      throw new Error('I18nManager must be initialized before switching locales');
    }

    if (!this.registeredLocales.has(newLocale)) {
      logger.warn(`Locale '${newLocale}' is not registered. Available locales:`, Array.from(this.registeredLocales));
      return;
    }

    locale.set(newLocale);
    
    // Apply any stored overrides for this locale
    this.applyStoredOverrides(newLocale);
  }

  public getCurrentLocale(): string | null | undefined {
    if (!this.isInitialized) {
      return null;
    }
    
    let currentLocale: string | null | undefined;
    const unsubscribe = locale.subscribe(value => {
      currentLocale = value;
    });
    unsubscribe(); // Clean up the subscription immediately
    
    return currentLocale;
  }

  public getSupportedLocales(): string[] {
    return Array.from(this.registeredLocales);
  }

  public isLocaleSupported(localeToCheck: string): boolean {
    return this.registeredLocales.has(localeToCheck);
  }

  /**
   * Override specific translation keys for a given locale
   * @param locale The locale to override translations for
   * @param overrides Object containing the translation key-value pairs to override
   * @example
   * // Override single translation
   * i18nManager.overrideTranslations('en', { 'welcome': 'Hello there!' });
   * 
   * // Override nested translations
   * i18nManager.overrideTranslations('en', { 
   *   'buttons': { 'submit': 'Send Now' },
   *   'messages.error': 'Something went wrong!'
   * });
   */
  public overrideTranslations(targetLocale: SupportedLocale | string, overrides: TranslationOverrides): void {
    if (!this.registeredLocales.has(targetLocale)) {
      logger.warn(`Cannot override translations for unsupported locale: ${targetLocale}`);
      return;
    }

    // Store the overrides
    const existingOverrides = this.translationOverrides.get(targetLocale) || {};
    const mergedOverrides = this.deepMerge(existingOverrides, overrides);
    this.translationOverrides.set(targetLocale, mergedOverrides);

    // Apply the overrides immediately if the locale is supported
    addMessages(targetLocale, overrides);

    logger.log(`Applied translation overrides for locale: ${targetLocale}`, overrides);
  }

  /**
   * Remove specific translation overrides for a locale
   * @param locale The locale to remove overrides from
   * @param keys Array of translation keys to remove (optional - if not provided, removes all overrides)
   */
  public removeTranslationOverrides(targetLocale: SupportedLocale | string, keys?: string[]): void {
    if (!this.translationOverrides.has(targetLocale)) {
      return;
    }

    if (!keys) {
      // Remove all overrides for this locale
      this.translationOverrides.delete(targetLocale);
      logger.log(`Removed all translation overrides for locale: ${targetLocale}`);
    } else {
      // Remove specific keys
      const overrides = this.translationOverrides.get(targetLocale)!;
      keys.forEach(key => {
        this.deleteNestedProperty(overrides, key);
      });
      
      if (Object.keys(overrides).length === 0) {
        this.translationOverrides.delete(targetLocale);
      }
      
      logger.log(`Removed translation overrides for keys: ${keys.join(', ')} in locale: ${targetLocale}`);
    }
  }

  /**
   * Get current translation overrides for a locale
   * @param locale The locale to get overrides for
   * @returns The current overrides object, or undefined if none exist
   */
  public getTranslationOverrides(targetLocale: SupportedLocale | string): TranslationOverrides | undefined {
    return this.translationOverrides.get(targetLocale);
  }

  /**
   * Get all translation overrides for all locales
   * @returns Map of all current overrides
   */
  public getAllTranslationOverrides(): Map<string, TranslationOverrides> {
    return new Map(this.translationOverrides);
  }

  /**
   * Apply stored overrides when switching to a locale
   * @param targetLocale The locale to apply overrides for
   */
  private applyStoredOverrides(targetLocale: string): void {
    const overrides = this.translationOverrides.get(targetLocale);
    if (overrides) {
      addMessages(targetLocale, overrides);
    }
  }

  /**
   * Deep merge two objects
   */
  private deepMerge(target: any, source: any): any {
    const result = { ...target };
    
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = this.deepMerge(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
    
    return result;
  }

  /**
   * Delete a nested property from an object using dot notation
   */
  private deleteNestedProperty(obj: any, path: string): void {
    const keys = path.split('.');
    let current = obj;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!(keys[i] in current)) {
        return;
      }
      current = current[keys[i]];
    }
    
    delete current[keys[keys.length - 1]];
  }
}

// Create a singleton instance for easy use
export const i18nManager = new I18nManager();

// Don't auto-initialize - let main.ts control when to init
